<template>
  <SettingsModal
    :headline="$t('results.filter')"
    icon="filter"
    :tracking="{
      customMessage: trackingOptions,
      customTopic: $t('results.filter'),
      topicKey: 'results-filter',
    }"
    @close="handleClose"
  >
    <div>
      <LcSelectList v-bind="{ options }" v-model:value="selectedOptions" multiple />
    </div>

    <template #footer>
      <template v-if="isLoading">
        <LcSkeleton height="2.5rem" radius="full" width="15rem" />
        <LcSkeleton height="2.5rem" radius="full" width="30rem" />
      </template>

      <template v-else>
        <LcButton
          class="button reset px-150"
          :class="{
            invisible: !hasSelectedOptions,
          }"
          :disabled="!hasSelectedOptions"
          variant="tertiary"
          @click="handleResetAll"
        >
          {{ $t('results.filter.reset') }}
        </LcButton>

        <LcButton class="button confirm" @click="handleShowSelected">
          {{
            hasSelectedOptions ? $t('results.filter.show.selected') : $t('results.filter.show.all')
          }}
        </LcButton>
      </template>
    </template>
  </SettingsModal>
</template>

<script lang="ts" setup>
import type { LcSelectListOption } from '@lottocom/frontend-components'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

type ResultsFilterModalEmit = {
  (e: 'close'): void
  (e: 'update:value', value: ResultsFilterModalOption['value'][]): void
}

type ResultsFilterModalOption = LcSelectListOption<LotteryId>

type ResultsFilterModalProps = {
  value?: ResultsFilterModalOption['value'][]
}

const emit = defineEmits<ResultsFilterModalEmit>()
const { value = [] } = defineProps<ResultsFilterModalProps>()

const { t } = useI18n()
const licensedTerritory = useLicensedTerritory({ raw: true })

const selectedOptions = ref<ResultsFilterModalOption['value'][]>(value)

const { loading: isLoading, result } = useLotteriesQuery({
  fetchPolicy: 'network-only',
  licensedTerritory,
})

const hasSelectedOptions = computed(() => selectedOptions.value.length > 0)
const options = computed(
  () =>
    result.value?.lotteries.map<ResultsFilterModalOption>((lottery) => ({
      label: t(`lottery.name.${lottery.id}`),
      value: lottery.id,
    })) ?? [],
)
const trackingOptions = computed(() => options.value.map(({ label }) => label).join(', '))

const emitUpdateEvent = () => {
  emit('update:value', selectedOptions.value)
}

const handleClose = () => {
  handleReset(true)
  handleShowSelected()
}

const handleReset = (isResetToInitialValue?: boolean) => {
  selectedOptions.value = isResetToInitialValue ? value : []
}

const handleResetAll = () => {
  handleReset(false)
  emitUpdateEvent()
}

const handleShowSelected = () => {
  emit('close')
  emitUpdateEvent()
}

watch(
  () => value,
  (newValue) => {
    selectedOptions.value = newValue
  },
)
</script>

<style lang="scss" scoped>
.button {
  &.confirm {
    min-width: 14rem;

    @include media-tablet {
      min-width: 12rem;
    }
  }

  &.reset {
    &.invisible {
      visibility: hidden;
    }
  }
}
</style>
